import { Box, Stack, FormControl, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { RootState } from '../../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';

import { dateFormatter } from '../../../../utils/timeFormat';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { patchUnmatchMember, setEndDate, setStartDate, getAlertsByMatchedDates, setRows, getAlertHistory } from '../../../../store/hie.slice';
import Button from '@mui/material/Button';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Authorization from '../../../../utils/Authorization';
import MatchedHistoryModal from './MatchedHistoryModal';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import { isDefaultDate } from '../../../../utils/common';
import { HIEAlertTypeEnum } from '../../../../Enum/HIEAlertTypeEnum';

const History = () => {
    const dispatch = useAppDispatch();
    const auth = new Authorization();

    const { alertHistory, rows, startDate, endDate } = useSelector((state: RootState) => state.hie);

    const [openHistory, setOpenHistory] = useState(false);
    const [matchedHistory, setMatchedHistory] = useState([]);

    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [confirmationModalTitle, setConfirmationModalTitle] = useState('');
    const [confirmationModalMessage, setConfirmationModalMessage] = useState('');
    const [alertId, setAlertId] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    const openVeiwHistoryHandler = (params) => {
        setMatchedHistory(params);
        setOpenHistory(true);
    };

    const closeViewHistoryHandler = () => {
        setMatchedHistory([]);
        setOpenHistory(false);
    };

    const openConfirmationModalHandler = (params) => {
        setConfirmationModalTitle('Confirm Un-Match');
        setConfirmationModalMessage(`Are you sure you wish to Un-Match ${params.row.LastName}, ${params.row.FirstName}?`);
        setAlertId(params.row.Id);
        setConfirmationModalOpen(true);
    };

    const closeConfirmationModalHandler = () => {
        setConfirmationModalOpen(false);
        setAlertId('');
    };

    const unMatchSelectedMemberHandler = () => {
        dispatch(patchUnmatchMember(alertId, auth.UserName));
        closeConfirmationModalHandler();
    };

    const getAlertsByMatchedDatesHandler = () => {
        dispatch(getAlertsByMatchedDates(dateFormatter(startDate), dateFormatter(endDate)));
    };

    useEffect(() => {
        dispatch(getAlertHistory());
    }, [dispatch]);

    useEffect(() => {
        if (alertHistory.length > 0) {
            dispatch(setRows(alertHistory));
        }
    }, [dispatch, alertHistory]);

    const columns: GridColDef[] = [
        {
            field: 'MemberId',
            headerName: 'Un-Match Alert',
            minWidth: 140,
            renderCell: (params: GridRenderCellParams) => {
                return params.row.MemberId ? (
                    <Button variant="contained" onClick={(e) => openConfirmationModalHandler(params)}>
                        Unmatch
                    </Button>
                ) : (
                    'Not Matched'
                );
            },
        },
        {
            field: 'MatchedDate',
            headerName: 'Matched Date',
            minWidth: 140,
            valueGetter: (value, row) => `${isDefaultDate(row.MatchedDate) ? 'N/A' : dateFormatter(new Date(row.MatchedDate))}`,
        },
        {
            field: 'History',
            headerName: 'Alert History',
            minWidth: 140,
            renderCell: (params: GridRenderCellParams) => {
                return params.row.History ? (
                    <Tooltip title="View History">
                        <Button startIcon={<WorkHistoryIcon />} onClick={() => openVeiwHistoryHandler(params.row.History)}></Button>
                    </Tooltip>
                ) : null;
            },
        },
        {
            field: 'Patient',
            headerName: 'Name',
            minWidth: 220,
            valueGetter: (value, row) => `${row.LastName}, ${row.FirstName} `,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 140,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? 'N/A' : dateFormatter(new Date(row.DOB))}`,
        },
        {
            field: 'CHPDownloadDate',
            headerName: 'Download Date',
            minWidth: 160,
            valueGetter: (value, row) => `${isDefaultDate(row.CHPDownloadDate) ? 'N/A' : dateFormatter(new Date(row.CHPDownloadDate))}`,
        },
        // {
        //     field: 'AlertType',
        //     headerName: 'Alert Type',
        //     minWidth: 120,
        // },
        {
            field: 'HIEAlertType',
            headerName: 'Alert Type',
            minWidth: 120,
            valueGetter: (value, row) => `${HIEAlertTypeEnum[row.HIEAlertType]}`,
        },
        {
            field: 'VisitType',
            headerName: 'Visit Type',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'SendingFacility',
            headerName: 'Sending Facility',
            minWidth: 280,
        },
        {
            field: 'AdmitDate',
            headerName: 'Admission Date',
            minWidth: 160,
            valueGetter: (value, row) => `${isDefaultDate(row.AdmitDate) ? 'N/A' : dateFormatter(new Date(row.AdmitDate))}`,
        },
        {
            field: 'AdmitTime',
            headerName: 'Admission Time',
            minWidth: 160,
            valueGetter: (value, row) => `${new Date(row.AdmitTime).toLocaleTimeString()}`,
        },
        {
            field: 'DischargeDate',
            headerName: 'Discharge Date',
            minWidth: 160,
            valueGetter: (value, row) => `${isDefaultDate(row.DischargeDate) ? 'N/A' : dateFormatter(new Date(row.DischargeDate))}`,
        },
        {
            field: 'DischargeTime',
            headerName: 'Discharge Time',
            minWidth: 160,
            valueGetter: (value, row) => `${new Date(row.DischargeTime).toLocaleTimeString()}`,
        },
    ];

    return (
        <>
            <ConfirmationModal
                confirmationModalMessage={confirmationModalMessage}
                handleClose={closeConfirmationModalHandler}
                open={confirmationModalOpen}
                handleAction={unMatchSelectedMemberHandler}
                title={confirmationModalTitle}
                handleCancel={() => setConfirmationModalOpen(false)}
            />
            <MatchedHistoryModal open={openHistory} handleClose={closeViewHistoryHandler} matchedHistory={matchedHistory} />
            <Box>
                <Grid display={'flex'} container p={5}>
                    <Grid size={2} p={2}>
                        <Typography variant="body1" marginBottom={2} fontWeight={'bold'}>
                            Search by Alert Download Date
                        </Typography>
                        <Stack>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <FormControl fullWidth>
                                    <DesktopDatePicker
                                        label="Start Date:"
                                        value={startDate}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => {
                                            dispatch(setStartDate(newValue));
                                        }}
                                        slotProps={{ textField: { required: true } }}
                                    />
                                </FormControl>
                                <FormControl fullWidth sx={{ marginY: 2 }}>
                                    <DesktopDatePicker
                                        label="End Date:"
                                        value={endDate}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => {
                                            dispatch(setEndDate(newValue));
                                        }}
                                        slotProps={{ textField: { required: true } }}
                                    />
                                </FormControl>
                            </LocalizationProvider>
                            <Button
                                sx={{ marginY: 2 }}
                                className="button-120"
                                disabled={!startDate || !endDate}
                                variant="contained"
                                onClick={getAlertsByMatchedDatesHandler}
                            >
                                Search
                            </Button>
                        </Stack>
                    </Grid>
                    <Grid size={10} p={2}>
                        <div style={{ width: '100%' }}>
                            <DataGrid
                                autoHeight
                                rows={rows}
                                columns={columns}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[5]}
                                getRowId={(row) => row.Id}
                                sx={{
                                    [`& .${gridClasses.cell}`]: {
                                        py: 1,
                                    },
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default History;
